import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api-client';
import { Eye, EyeOff, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [form, setForm] = useState({ password: '', confirm: '' });
  const [showPw, setShowPw] = useState(false);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenEmail, setTokenEmail] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const strength = form.password.length >= 12 ? 'strong' : form.password.length >= 8 ? 'medium' : 'weak';

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError('No reset token provided. Please request a new password reset link.');
        setVerifying(false);
        return;
      }
      
      try {
        const response = await api.verifyResetToken(token);
        if (response.valid) {
          setTokenValid(true);
          setTokenEmail(response.email);
        } else {
          setError(response.error || 'Invalid or expired token. Please request a new password reset link.');
        }
      } catch (err) {
        setError('Failed to verify token. Please try again or request a new link.');
      }
      setVerifying(false);
    };
    
    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) return;
    
    setLoading(true);
    setError('');
    
    try {
      const response = await api.resetPassword(token, form.password);
      if (response.success) {
        setDone(true);
        setTimeout(() => navigate('/signin'), 2000);
      } else {
        setError(response.error || 'Failed to reset password. Please try again.');
      }
    } catch (err) {
      setError(err.message || 'Failed to reset password. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4 pt-20">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="text-emerald-500 font-mono font-bold text-xl inline-block mb-6">
            <span className="text-cyan-400">{'>'}</span>_ClawStinger
          </Link>
          <h1 data-testid="reset-title" className="text-2xl font-bold text-white font-mono mb-2">Set new password</h1>
          <p className="text-sm text-zinc-500">Choose a strong password</p>
        </div>

        <div className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-8">
          {verifying ? (
            <div className="text-center py-8">
              <Loader2 size={32} className="text-emerald-400 mx-auto mb-4 animate-spin" />
              <p className="text-zinc-400 font-mono text-sm">Verifying reset link...</p>
            </div>
          ) : !tokenValid ? (
            <div data-testid="token-invalid" className="text-center py-4">
              <AlertCircle size={40} className="text-red-400 mx-auto mb-4" />
              <p className="text-white font-mono mb-2">Invalid Reset Link</p>
              <p className="text-sm text-zinc-500 mb-4">{error}</p>
              <Link to="/forgot-password" className="text-emerald-400 text-sm font-mono hover:underline">
                Request a new reset link
              </Link>
            </div>
          ) : done ? (
            <div data-testid="reset-success" className="text-center py-4">
              <CheckCircle size={40} className="text-emerald-400 mx-auto mb-4" />
              <p className="text-white font-mono mb-2">Password updated</p>
              <p className="text-sm text-zinc-500">Redirecting to sign in...</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {tokenEmail && (
                <div className="bg-zinc-900 border border-zinc-800 rounded-sm px-4 py-3 mb-4">
                  <p className="text-xs text-zinc-500 font-mono">Resetting password for:</p>
                  <p className="text-sm text-white font-mono">{tokenEmail}</p>
                </div>
              )}
              
              {error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-sm px-4 py-3">
                  <p className="text-sm text-red-400 font-mono">{error}</p>
                </div>
              )}
              
              <div className="relative">
                <label className="block text-xs font-mono text-zinc-500 mb-1.5">New Password</label>
                <input data-testid="reset-password-input" type={showPw ? 'text' : 'password'} required minLength={8} value={form.password} onChange={e => setForm({...form, password: e.target.value})} className="w-full bg-[#111] border border-zinc-800 text-white rounded-sm px-4 py-2.5 text-sm font-mono focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/50 outline-none transition-all pr-10 placeholder-zinc-600" placeholder="Min 8 characters" />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-8 text-zinc-600 hover:text-zinc-400">{showPw ? <EyeOff size={16} /> : <Eye size={16} />}</button>
              </div>
              {form.password && (
                <div data-testid="password-strength" className="flex items-center gap-2">
                  <div className="flex-1 h-1 bg-zinc-800 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all ${strength === 'strong' ? 'w-full bg-emerald-500' : strength === 'medium' ? 'w-2/3 bg-yellow-500' : 'w-1/3 bg-red-500'}`} />
                  </div>
                  <span className={`text-xs font-mono ${strength === 'strong' ? 'text-emerald-400' : strength === 'medium' ? 'text-yellow-400' : 'text-red-400'}`}>{strength}</span>
                </div>
              )}
              <div>
                <label className="block text-xs font-mono text-zinc-500 mb-1.5">Confirm Password</label>
                <input data-testid="reset-confirm-input" type="password" required value={form.confirm} onChange={e => setForm({...form, confirm: e.target.value})} className="w-full bg-[#111] border border-zinc-800 text-white rounded-sm px-4 py-2.5 text-sm font-mono focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/50 outline-none transition-all placeholder-zinc-600" placeholder="Repeat password" />
                {form.confirm && form.password !== form.confirm && (
                  <p className="text-xs text-red-400 mt-1 font-mono">Passwords don't match</p>
                )}
              </div>
              <button data-testid="reset-submit-btn" type="submit" disabled={loading || form.password !== form.confirm} className="w-full bg-emerald-500 text-black font-mono font-bold text-sm rounded-sm py-3 hover:bg-emerald-400 transition-all click-scale shadow-[0_0_15px_rgba(16,185,129,0.3)] disabled:opacity-50 disabled:cursor-not-allowed">
                {loading ? 'Updating...' : 'Update Password'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
